// Copyright (c) Ping. All rights reserved.
//
// Client-side "Saved Messages" store. Users save short canned messages
// (optionally titled) that they can drop into the composer for quick reuse
// — a fully local feature with no server component. Persisted to
// `localStorage` (web/desktop) under a single key, mirroring the shape of
// the scheduled-message store (`scheduledMessages.ts`): an in-memory cache
// plus a listener set so every mounted composer stays in sync.
//
// Saved messages are GLOBAL (not conversation-scoped) — the same quick
// replies are available in every conversation. A no-op on platforms
// without `localStorage`; the in-memory cache still works for the session.

export interface SavedMessage {
  readonly id: string;
  /** Optional short title / shortcut shown above the body in the list. */
  readonly title?: string;
  /** The message body inserted into the composer. */
  readonly text: string;
  /** Epoch ms the entry was created (for stable, newest-first ordering). */
  readonly createdAtMs: number;
}

const STORAGE_KEY = 'ping.composer.saved';
/** Cap the body length so a pathological paste can't bloat localStorage. */
export const SAVED_MESSAGE_MAX_LENGTH = 500;

function storage(): Storage | null {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    /* sandboxed / SSR */
  }
  return null;
}

function loadAll(): SavedMessage[] {
  const s = storage();
  if (s === null) return [];
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (raw === null || raw.length === 0) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is SavedMessage =>
        m !== null &&
        typeof m === 'object' &&
        typeof (m as SavedMessage).id === 'string' &&
        typeof (m as SavedMessage).text === 'string' &&
        typeof (m as SavedMessage).createdAtMs === 'number',
    );
  } catch {
    return [];
  }
}

function saveAll(list: SavedMessage[]): void {
  const s = storage();
  if (s === null) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* quota / disabled — best-effort */
  }
}

// In-memory mirror so reads are cheap and every composer sees the same list.
let cache: SavedMessage[] | null = null;
function all(): SavedMessage[] {
  if (cache === null) cache = loadAll();
  return cache;
}
function commit(next: SavedMessage[]): void {
  cache = next;
  saveAll(next);
  for (const l of listeners) l();
}

const listeners = new Set<() => void>();
let idCounter = 0;

/** Subscribe to any change in the saved list. Returns an unsubscribe fn. */
export function subscribeSavedMessages(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * All saved messages, newest-first. Entries are always appended, so the tail
 * is the newest — a reversed copy gives a stable newest-first order without
 * depending on `createdAtMs` (which can tie within the same millisecond).
 */
export function listSavedMessages(): SavedMessage[] {
  return [...all()].reverse();
}

/**
 * Persist a new saved message. `text` is trimmed and length-capped; an empty
 * body is rejected (returns `null`). `title` is optional and trimmed away when
 * blank.
 */
export function addSavedMessage(input: {
  text: string;
  title?: string;
}): SavedMessage | null {
  const text = input.text.trim().slice(0, SAVED_MESSAGE_MAX_LENGTH);
  if (text.length === 0) return null;
  const title = (input.title ?? '').trim();
  const now = Date.now();
  const msg: SavedMessage = {
    id: `saved_${now}_${idCounter++}`,
    ...(title.length > 0 ? { title } : {}),
    text,
    createdAtMs: now,
  };
  commit([...all(), msg]);
  return msg;
}

/** Remove a saved message by id. */
export function removeSavedMessage(id: string): void {
  const next = all().filter((m) => m.id !== id);
  if (next.length !== all().length) commit(next);
}

// Test-only reset hook — clears in-memory state so unit tests don't leak
// the cache / listeners across cases.
export function __resetSavedForTests(): void {
  cache = null;
  listeners.clear();
  idCounter = 0;
}
